import React from 'react';
import { Dialog, Portal } from 'react-native-paper';
import { t, Trans } from '@lingui/macro';

import Button from '@src/components/Button';
import { Body1 } from '@src/components/Typography';
import { ReportType } from '@src/enums';
import { i18n } from '@src/i18n';
import { Post } from '@src/types';

import { ReportPicker } from './styled';
import useReport from './useReport';

type Props = {
  post: Post;
  visible?: boolean;
  onDismiss?: () => void;
  onSubmit?: () => void;
};

const ReportModal: React.FC<Props> = ({
  post,
  visible,
  onDismiss,
  onSubmit,
}) => {
  const { handleReport } = useReport();

  const [reportType, setReportType] = React.useState<ReportType>(
    ReportType.Other
  );
  const [showReportTypePicker, setShowReportTypePicker] = React.useState(false);
  const reportList = React.useMemo(
    () => [
      {
        label: i18n._(t`Fraud`),
        value: ReportType.Fraud,
      },
      {
        label: i18n._(t`HateSpeech`),
        value: ReportType.HateSpeech,
      },
      {
        label: i18n._(t`Illegal`),
        value: ReportType.Illegal,
      },
      {
        label: i18n._(t`Nudity`),
        value: ReportType.Nudity,
      },
      {
        label: i18n._(t`SelfInjury`),
        value: ReportType.SelfInjury,
      },
      {
        label: i18n._(t`Spam`),
        value: ReportType.Spam,
      },
      {
        label: i18n._(t`Violence`),
        value: ReportType.Violence,
      },
      {
        label: i18n._(t`Other`),
        value: ReportType.Other,
      },
    ],
    []
  );

  const setReportTypeHandler = React.useCallback((_reportType: ReportType) => {
    setReportType(_reportType);
  }, []);

  const handleReportPost = React.useCallback(async () => {
    if (!post) {
      return;
    }
    try {
      await handleReport({
        entityType: 'CHECK_IN',
        id: post.id,
        reportType: reportType,
        text: '',
      });
      onSubmit?.();
    } catch (error) {
      console.log({ error });
    }
  }, [post, handleReport, reportType, onSubmit]);

  return (
    <Portal>
      <Dialog visible={!!visible} onDismiss={onDismiss}>
        <>
          <Dialog.Title>
            <Trans>Report Post</Trans>
          </Dialog.Title>
          <Dialog.Content>
            <Body1>
              <Trans>What do you want to report?</Trans>
            </Body1>
            <ReportPicker
              value={reportType}
              setValue={setReportTypeHandler}
              list={reportList}
              visible={showReportTypePicker}
              placeholder={i18n._(t`Select Report Type`)}
              onShow={() => setShowReportTypePicker(true)}
              onDismiss={() => setShowReportTypePicker(false)}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={handleReportPost}>
              <Trans>Submit Report</Trans>
            </Button>
          </Dialog.Actions>
        </>
      </Dialog>
    </Portal>
  );
};

export default ReportModal;
